import React from "react";
import { createPortal } from "react-dom";

const PreviewCtx = React.createContext(null);
export { PreviewCtx };

const clampInstagramAudioVolume = (value, fallback = 100) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.min(100, Math.round(n)));
};

// console.log("#### PreviewRoot ####");

export class PreviewRoot extends React.Component {
  state = { open: false, content: null, data: null, rect: null, align: "right" };

  open  = (payload) => this.setState({ open: true, ...payload });
  close = () => this.setState({ open: false });

  render() {
    return (
      <PreviewCtx.Provider value={{ open: this.open, close: this.close }}>
        {this.props.children}
        {this.state.open && (
          <PreviewPortal
            content={this.state.content}
            data={this.state.data}
            rect={this.state.rect}
            align={this.state.align}
            onClose={this.close}
          />
        )}
      </PreviewCtx.Provider>
    );
  }
}

export function withPreview(Component) {
  return function Wrapped(props) {
    return (
      <PreviewCtx.Consumer>
        {(value) => <Component {...props} preview={value} />}
      </PreviewCtx.Consumer>
    );
  };
}

export class PreviewPortal extends React.Component {
  ref = React.createRef();
  videoRef = React.createRef();
  audioRef = React.createRef();
  state = { left: -9999, top: -9999, slideIdx: 0 };

  componentDidMount() {
    // console.log("#### PreviewPortal componentDidMount ####");
    console.log("this.props.data", this.props.data)
    this.updatePosition();
    this.syncReelsPreviewVolumes();
    window.addEventListener("scroll", this.props.onClose);
    window.addEventListener("resize", this.props.onClose);
  }
  componentDidUpdate(prev, prevState) {
    // console.log("#### PreviewPortal componentDidUpdate ####");
    if (prev.rect !== this.props.rect) this.updatePosition();
    if (prev.data !== this.props.data) {
      this.stopReelsPreviewAudio(true);
      this.setState({ slideIdx: 0 }); // 트리거 바뀌면 리셋
    } else if (prevState.slideIdx !== this.state.slideIdx) {
      this.stopReelsPreviewAudio(true);
    }
  }
  componentWillUnmount() {
    // console.log("#### PreviewPortal componentWillUnmount ####");
    this.stopReelsPreviewAudio(true);
    window.removeEventListener("scroll", this.props.onClose);
    window.removeEventListener("resize", this.props.onClose);
  }

  updatePosition() {
    const { rect, align } = this.props;
    const el = this.ref.current;
    if (!rect || !el) return;

    const gap = 10;
    const bw = el.offsetWidth;
    const bh = el.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left = align === "right" ? rect.right + gap : rect.left - bw - gap;
    let top = rect.top;

    if (left + bw + gap > vw) left = rect.left - bw - gap;
    if (left < gap) left = gap;
    if (top + bh + gap > vh) top = vh - bh - gap;
    if (top < gap) top = gap;

    this.setState({ left, top });
  }

  getReelsAudioState = () => {
    const data = this.props.data || {};
    return data.reelsAudio && typeof data.reelsAudio === "object" ? data.reelsAudio : {};
  };

  hasInstagramAudio = () => {
    const audioState = this.getReelsAudioState();
    return (audioState.mode === "MUSIC" || audioState.mode === "ORIGINAL_SOUND") && !!audioState.audioId;
  };

  syncReelsPreviewVolumes = () => {
    const audioState = this.getReelsAudioState();
    const hasInstagramAudio = this.hasInstagramAudio();
    const video = this.videoRef.current;
    const audio = this.audioRef.current;

    if (video) {
      if (hasInstagramAudio) {
        const videoVolume = clampInstagramAudioVolume(audioState.videoVolume, 100);
        video.muted = videoVolume === 0;
        video.volume = videoVolume / 100;
      } else {
        video.muted = false;
        video.volume = 1;
      }
    }

    if (audio) {
      audio.volume = hasInstagramAudio
        ? clampInstagramAudioVolume(audioState.audioVolume, 100) / 100
        : 0;
    }
  };

  syncReelsPreviewAudioTime = (video) => {
    const audioState = this.getReelsAudioState();
    const audio = this.audioRef.current;
    if (!video || !audio || !audioState.previewUrl) return;

    try {
      const duration = Number(audio.duration);
      if (Number.isFinite(duration) && duration > 0) {
        const next = Number(video.currentTime || 0) % duration;
        if (Math.abs(Number(audio.currentTime || 0) - next) > 0.35) {
          audio.currentTime = next;
        }
      }
    } catch (e) {}
  };

  handleReelsPreviewPlay = async (event) => {
    const video = event.currentTarget;
    this.syncReelsPreviewVolumes();

    const audioState = this.getReelsAudioState();
    const audio = this.audioRef.current;
    if (!audio || !audioState.previewUrl || !audioState.audioId) return;

    this.syncReelsPreviewAudioTime(video);
    try {
      await audio.play();
    } catch (e) {
      // 브라우저 자동재생 정책 또는 저장된 미리듣기 URL 제한 시 영상 재생은 유지한다.
      console.warn("Instagram 목록 음악 미리보기 동기 재생 실패:", e);
    }
  };

  handleReelsPreviewPause = () => {
    const audio = this.audioRef.current;
    if (audio) {
      try { audio.pause(); } catch (e) {}
    }
  };

  handleReelsPreviewSeeked = (event) => {
    this.syncReelsPreviewAudioTime(event.currentTarget);
  };

  handleReelsPreviewTimeUpdate = (event) => {
    const video = event.currentTarget;
    const audio = this.audioRef.current;
    if (!audio || audio.paused) return;
    this.syncReelsPreviewAudioTime(video);
  };

  handleReelsPreviewEnded = () => {
    this.stopReelsPreviewAudio(true);
  };

  stopReelsPreviewAudio = (reset = false) => {
    const audio = this.audioRef.current;
    if (!audio) return;

    try {
      audio.pause();
      if (reset) audio.currentTime = 0;
    } catch (e) {}
  };

  next = (n) => {
    const slides = (this.props.data && this.props.data.slides) || [];
    if (!slides.length) return;
    this.setState((p) => {
      const total = slides.length;
      const next = ((p.slideIdx + n) % total + total) % total;
      return { slideIdx: next };
    });
  };

  handleNavPointerDown = (e) => {
    // 모바일 터치에서 부모/외부 레이어가 먼저 이벤트를 받아 프리뷰가 닫히는 것을 방지
    e.stopPropagation();
  };

  handleNavPointerUp = (e, direction) => {
    e.preventDefault();
    e.stopPropagation();
    this.next(direction);
  };

  handleNavClick = (e, direction) => {
    e.preventDefault();
    e.stopPropagation();

    // 마우스/터치는 pointerup에서 이미 처리한다.
    // 키보드 Enter/Space로 발생한 click(detail === 0)만 여기서 처리한다.
    if (e.detail === 0) {
      this.next(direction);
    }
  };

  handleIndicatorPointerUp = (e, slideIdx) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ slideIdx });
  };

  handleIndicatorClick = (e, slideIdx) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.detail === 0) {
      this.setState({ slideIdx });
    }
  };

  render() {
    const data = this.props.data || {};
    const rawSlides = data.slides || [];
    const slides = rawSlides
      .map((slide) => {
        if (typeof slide === "string") {
          const value = slide || "";
          const isVideo = /\.(mp4|mov|m4v|webm|avi|mpeg|mpg)(\?.*)?$/i.test(value);

          return {
            type: isVideo ? "video" : "image",
            url: value,
            thumbnail: value,
          };
        }

        const url = slide?.url || slide?.source_url || "";
        const thumbnail = slide?.thumbnail || slide?.preview_image_url || slide?.thumbnail_url || url;
        const isVideo =
          slide?.type === "video" ||
          /\.(mp4|mov|m4v|webm|avi|mpeg|mpg)(\?.*)?$/i.test(url);

        return {
          type: isVideo ? "video" : "image",
          url,
          thumbnail,
          // 관리자 인스타그램 이용 현황에서만 사용하는 슬라이드별 파일 생성/유입 방식 뱃지.
          // 일반 InstagramPostList_v2는 이 값을 넘기지 않으므로 기존 UI에는 영향이 없다.
          fileTypeBadge: slide?.fileTypeBadge || slide?.content_source_type || slide?.item_content_source_type || "",
        };
      })
      .filter((slide) => slide.url || slide.thumbnail);

    // console.log("### slides", slides);
    const hasSlides = slides.length > 0;
    const cur = hasSlides ? slides[this.state.slideIdx] : null;
    const isVideo = cur?.type === "video";
    const reelsAudio = data.reelsAudio && typeof data.reelsAudio === "object" ? data.reelsAudio : {};
    const hasInstagramAudio =
      isVideo
      && (reelsAudio.mode === "MUSIC" || reelsAudio.mode === "ORIGINAL_SOUND")
      && !!reelsAudio.audioId;
    const videoVolume = clampInstagramAudioVolume(reelsAudio.videoVolume, 100);
    // 필요한 화면에서만 넘겨 사용하는 선택형 파일 종류 뱃지.
    // 기존 InstagramPostList_v2는 fileTypeBadge를 넘기지 않으므로 UI 변화 없음.
    const fileTypeBadge = cur?.fileTypeBadge || data.fileTypeBadge || data.contentSourceType || "";

    const node = (
      <>
        <style>
          {`
            .preview-box .fa-heart {
              color: #ef4444;
            }

            .preview-box .preview-nav-button {
              touch-action: manipulation;
              -webkit-tap-highlight-color: transparent;
              user-select: none;
              -webkit-user-select: none;
            }

            .preview-box .preview-indicator-button {
              touch-action: manipulation;
              -webkit-tap-highlight-color: transparent;
            }

            @media (pointer: coarse) {
              .preview-box .preview-nav-button {
                width: 44px !important;
                height: 44px !important;
              }
              .preview-box .preview-indicator-button {
                width: 18px !important;
                height: 18px !important;
                display: inline-flex !important;
                align-items: center !important;
                justify-content: center !important;
              }
            }
          `}
        </style>
        <div
          ref={this.ref}
          className="preview-box instagram-post-list"
          style={{ position:"fixed", left:this.state.left, top:this.state.top, zIndex:10000, overflow:"visible" }}
          onPointerEnter={() => {/* 열림 유지 */}}
          onPointerLeave={(e) => {
            // 모바일/태블릿 터치에서는 relatedTarget이 null이 되는 경우가 많아
            // 버튼을 누르는 순간 프리뷰가 닫힐 수 있으므로 마우스에서만 hover-close 처리한다.
            if (e.pointerType && e.pointerType !== "mouse") return;

            const rt = e.relatedTarget || e.nativeEvent.relatedTarget;
            const safe = rt && (rt.closest?.(".img-thumbnail") || rt.closest?.(".thumb-media") || rt.closest?.(".preview-box"));
            if (!safe) this.props.onClose();
          }}
        >
          <div className="preview-surface" style={{ position:'relative'}}>
          <div
            style={{
              position:'relative',
              borderRadius:12,
              overflow:'hidden',
              background:'#fff',
              boxShadow:'0 8px 24px rgba(0,0,0,.08)'
            }}
          >
            {/* 1) 이미지 영역 */}
            <div
              style={{
                width:'100%',
                aspectRatio: data.image_ratio ? data.image_ratio : '1 / 1',
                background:'#f6f6f6',
                display:'flex',
                alignItems:'center',
                justifyContent:'center',
                position:'relative',
              }}
            >
              {cur ? (
                isVideo ? (
                  <>
                    <video
                      ref={this.videoRef}
                      src={cur.url}
                      poster={cur.thumbnail && cur.thumbnail !== cur.url ? cur.thumbnail : undefined}
                      controls
                      muted={hasInstagramAudio && videoVolume === 0}
                      playsInline
                      preload="metadata"
                      style={{
                        width:'100%',
                        height:'100%',
                        objectFit:'contain',
                        display:'block',
                        background:'#111827'
                      }}
                      onLoadedMetadata={this.syncReelsPreviewVolumes}
                      onPlay={this.handleReelsPreviewPlay}
                      onPause={this.handleReelsPreviewPause}
                      onSeeked={this.handleReelsPreviewSeeked}
                      onTimeUpdate={this.handleReelsPreviewTimeUpdate}
                      onEnded={this.handleReelsPreviewEnded}
                    />
                    <div
                      style={{
                        position:'absolute', top:10, right:10, padding:'4px 8px',
                        borderRadius:999, background:'rgba(17,24,39,.78)',
                        color:'#fff', fontSize:11, fontWeight:700, zIndex:2,
                        pointerEvents:'none'
                      }}
                    >
                      REELS
                    </div>
                    <audio
                      ref={this.audioRef}
                      src={hasInstagramAudio ? (reelsAudio.previewUrl || undefined) : undefined}
                      preload="metadata"
                      loop
                    />
                    {hasInstagramAudio ? (
                      <div
                        style={{
                          position:'absolute',
                          left:10,
                          right:10,
                          bottom:52,
                          display:'flex',
                          alignItems:'center',
                          gap:8,
                          padding:'8px 10px',
                          borderRadius:8,
                          background:'rgba(0,0,0,.62)',
                          color:'#fff',
                          pointerEvents:'none',
                          zIndex:4
                        }}
                      >
                        <span style={{ fontSize:16 }}>♫</span>
                        <div style={{ minWidth:0, flex:1 }}>
                          <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontSize:11, fontWeight:800 }}>
                            {reelsAudio.title || '선택한 음악'}
                          </div>
                          <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontSize:9, opacity:.78 }}>
                            {reelsAudio.artist || ''}
                          </div>
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:4, flexShrink:0 }}>
                          {!reelsAudio.previewUrl ? (
                            <span style={{ fontSize:9, fontWeight:700, padding:'3px 5px', borderRadius:4, background:'rgba(255,255,255,.16)', whiteSpace:'nowrap' }}>
                              미리듣기 불가
                            </span>
                          ) : null}
                          {videoVolume === 0 ? (
                            <span style={{ fontSize:9, fontWeight:700, padding:'3px 5px', borderRadius:4, background:'rgba(255,255,255,.16)', whiteSpace:'nowrap' }}>
                              영상 소리 꺼짐
                            </span>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                  </>
                ) : (
                  <img
                    src={cur.url}
                    alt={`slide-${this.state.slideIdx}`}
                    style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}
                  />
                )
              ) : null}

              {/* 파일 생성/유입 방식 뱃지: 현재 슬라이드의 fileTypeBadge 값을 우측 하단에 노출 */}
              {fileTypeBadge ? (
                <div
                  style={{
                    position:'absolute',
                    right:10,
                    bottom:isVideo ? (hasInstagramAudio ? 106 : 42) : 10,
                    maxWidth:'calc(100% - 20px)',
                    padding:'4px 9px',
                    borderRadius:999,
                    background:'rgba(17,24,39,.78)',
                    color:'#fff',
                    fontSize:11,
                    fontWeight:700,
                    lineHeight:1.2,
                    whiteSpace:'nowrap',
                    overflow:'hidden',
                    textOverflow:'ellipsis',
                    zIndex:3,
                    pointerEvents:'none',
                    boxShadow:'0 1px 4px rgba(0,0,0,.14)'
                  }}
                >
                  {fileTypeBadge}
                </div>
              ) : null}

              {/* 2) 슬라이드 인디케이터 */}
              {slides.length > 1 && (
                <div
                  style={{
                    position:'absolute', bottom:10, left:'50%', transform:'translateX(-50%)',
                    display:'flex', gap:6, zIndex:2
                  }}
                >
                  {slides.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      className="preview-indicator-button"
                      onPointerDown={(e) => e.stopPropagation()}
                      onPointerUp={(e) => this.handleIndicatorPointerUp(e, i)}
                      onClick={(e) => this.handleIndicatorClick(e, i)}
                      aria-label={`${i + 1}번 슬라이드`}
                      style={{
                        width:12, height:12, padding:0, border:'none', background:'transparent',
                        cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center'
                      }}
                    >
                      <span
                        style={{
                          width:6, height:6, borderRadius:'50%', display:'block',
                          background: i === this.state.slideIdx ? '#fff' : 'rgba(255,255,255,.6)'
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}

              {/* 3) 좌/우 네비게이션 */}
              {slides.length > 1 && (
                <>
                  <button
                    type="button"
                    className="preview-nav-button"
                    onPointerDown={this.handleNavPointerDown}
                    onPointerUp={(e) => this.handleNavPointerUp(e, -1)}
                    onClick={(e) => this.handleNavClick(e, -1)}
                    style={{
                      position:'absolute', left:10, top:'50%', transform:'translateY(-50%)',
                      width:32, height:32, borderRadius:'50%', border:'none',
                      background:'rgba(0,0,0,.45)', color:'#fff', cursor:'pointer', zIndex:5,
                      display:'flex', alignItems:'center', justifyContent:'center',
                      padding:0, fontSize:24, lineHeight:1
                    }}
                    aria-label="previous"
                  >‹</button>
                  <button
                    type="button"
                    className="preview-nav-button"
                    onPointerDown={this.handleNavPointerDown}
                    onPointerUp={(e) => this.handleNavPointerUp(e, 1)}
                    onClick={(e) => this.handleNavClick(e, 1)}
                    style={{
                      position:'absolute', right:10, top:'50%', transform:'translateY(-50%)',
                      width:32, height:32, borderRadius:'50%', border:'none',
                      background:'rgba(0,0,0,.45)', color:'#fff', cursor:'pointer', zIndex:5,
                      display:'flex', alignItems:'center', justifyContent:'center',
                      padding:0, fontSize:24, lineHeight:1
                    }}
                    aria-label="next"
                  >›</button>
                </>
              )}
            </div>

            {/* 4) 정보 패널 (캡션/일시/지표) */}
            {(() => {
              const d = data || {};
              const caption = d.caption || '';
              const hashtags = d.hashtags || '';
              const publishedAt = d.publishedAt || null;
              // Instagram 인사이트 (기존 구조 유지)
              const likes = d.metrics?.likes ?? null;
              const comments = d.metrics?.comments ?? null;
              const views = d.metrics?.views ?? d.metrics?.saves ?? null;

              // Threads 인사이트
              // - 권장 전달 구조: threadsMetrics: { likes, replies, views }
              // - 기존/백엔드 필드명을 직접 넘기는 경우도 호환
              const threadsLikes =
                d.threadsMetrics?.likes ??
                d.metrics?.threads?.likes ??
                d.metrics?.threads_like_cnt ??
                d.threads_like_cnt ??
                null;
              const threadsReplies =
                d.threadsMetrics?.replies ??
                d.metrics?.threads?.replies ??
                d.metrics?.threads_reply_cnt ??
                d.threads_reply_cnt ??
                null;
              const threadsViews =
                d.threadsMetrics?.views ??
                d.metrics?.threads?.views ??
                d.metrics?.threads_view_cnt ??
                d.threads_view_cnt ??
                null;

              const showInstagramMetrics =
                String(d.instagramPublishYn ?? d.instagram_publish_yn ?? 'Y').toUpperCase() !== 'N';
              const showThreadsMetrics =
                String(d.threadsPublishYn ?? d.threads_publish_yn ?? '').toUpperCase() === 'Y' ||
                threadsLikes !== null ||
                threadsReplies !== null ||
                threadsViews !== null;

              const fmt = (n) => (n === null || n === undefined) ? '-' : Number(n).toLocaleString();
              // console.log("### hashtags",hashtags || "없음");
              return (
                <div style={{ padding:'12px 14px', borderTop:'1px solid #efefef', background:'#fff' }}>
                  {/* 캡션 */}
                  {caption ? (
                    <p style={{ margin: 0, fontSize:14, fontWeight: 500, lineHeight: 1.45, color: '#111' }}>
                      {caption.split("\n").map((line, idx) => (
                        <React.Fragment key={idx}>
                          {line}
                          <br />
                        </React.Fragment>
                      ))}
                    </p>
                  ) : null}

                  {/* 해시태그 */}
                  {hashtags ? (
                    <p style={{ margin:'2px 0 0', fontSize:14, color:'rgb(0, 55, 107)' }}>
                        {hashtags}
                    </p>
                  ): null}

                  {/* 게시일시 */}
                  {publishedAt ? (
                    <p style={{ margin:'6px 0 0', fontSize:12, color:'#8e8e8e' }}>
                      {publishedAt}
                    </p>
                  ) : null}

                  {/* 플랫폼별 인사이트 */}
                  <div
                    style={{
                      display:'flex',
                      flexDirection:'column',
                      gap:7,
                      marginTop:8,
                      fontSize:12,
                      color:'#212529'
                    }}
                  >
                    {/* Instagram */}
                    {showInstagramMetrics ? (
                      <div style={{ display:'flex', alignItems:'center', gap:8, minHeight:24 }}>
                        <span
                          style={{
                            flex:'0 0 72px',
                            padding:'2px 7px',
                            border:'1px solid #e5e7eb',
                            borderRadius:999,
                            background:'#f8fafc',
                            color:'#7c3aed',
                            fontSize:10,
                            fontWeight:600,
                            textAlign:'center',
                            whiteSpace:'nowrap'
                          }}
                        >
                          Instagram
                        </span>
                        <div style={{ display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
                          <span><i className="fa fa-heart" aria-hidden="true"></i> {fmt(likes)}</span>
                          <span><i className="fa fa-comment" aria-hidden="true"></i> {fmt(comments)}</span>
                          <span><i className="fa fa-eye" aria-hidden="true"></i> {fmt(views)}</span>
                        </div>
                      </div>
                    ) : null}

                    {/* Threads: Threads 발행 정보 또는 인사이트 값이 있을 때 표시 */}
                    {showThreadsMetrics ? (
                      <div style={{ display:'flex', alignItems:'center', gap:8, minHeight:24 }}>
                        <span
                          style={{
                            flex:'0 0 72px',
                            padding:'2px 7px',
                            border:'1px solid #e5e7eb',
                            borderRadius:999,
                            background:'#f8fafc',
                            color:'#111827',
                            fontSize:10,
                            fontWeight:600,
                            textAlign:'center',
                            whiteSpace:'nowrap'
                          }}
                        >
                          Threads
                        </span>
                        <div style={{ display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
                          <span><i className="fa fa-heart" aria-hidden="true"></i> {fmt(threadsLikes)}</span>
                          <span><i className="fa fa-comment" aria-hidden="true"></i> {fmt(threadsReplies)}</span>
                          <span><i className="fa fa-eye" aria-hidden="true"></i> {fmt(threadsViews)}</span>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })()}
          </div>
          </div>
        </div>
      </>
    );
    return createPortal(node, document.body);
  }
}